import { useAsyncData, useNuxtApp, useRouteLocationWithLocale } from '#imports'
import { type Ref, computed, onScopeDispose, ref } from 'vue'

import type {
  AlgoliaLink,
  AlgoliaProduct,
} from '@backmarket/http-api/src/api-specs-search-reco/search/searchAlgolia.ts'
import type { SearchBarConfiguration } from '@backmarket/http-api/src/api-specs-search-reco/search/searchConfiguration.ts'
import { useAlgoliaSearchOptions } from '@backmarket/nuxt-layer-algolia/useAlgoliaSearchOptions'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import algoliasearch from 'algoliasearch'

import { useBrowsingHistory } from '~/scopes/browsing-history/composables/useBrowsingHistory'
import type { BrowsingHistoryEntry } from '~/scopes/browsing-history/types'
import { ROUTES as PRODUCT_LIST_ROUTES } from '~/scopes/product-list/route-names'
import {
  algoliaHitToLink,
  algoliaHitToProduct,
} from '~/scopes/search/algolia/algoliaFunctions'

import type { RecentlyViewedPage } from './useRecentlyViewedPages.types'

export function useRecentlyViewedPages(
  configuration: Ref<SearchBarConfiguration | undefined>,
) {
  const experiments = useExperiments()
  const app = useNuxtApp()
  const resolveLocalizedRoute = useRouteLocationWithLocale()

  const isBrowsingHistoryEnabled =
    experiments['experiment.browsingHistoryPanel'] === 'enabled'
  const algoliaSearchOption = useAlgoliaSearchOptions('search')
  const algoliaClient = computed(() => {
    if (!configuration.value) return null

    return algoliasearch('dummy_app_id', 'dummy_key', algoliaSearchOption)
  })

  // Browsing history state, fetched on demand when the recently viewed pages run.
  const historyEntries = ref<BrowsingHistoryEntry[]>([])
  const { fetch: fetchHistoryEntries } = useBrowsingHistory()

  async function fetchAllHistoryEntries() {
    const [pdpEntries, plpAndSearchEntries] = await Promise.all([
      fetchHistoryEntries({
        kind: ['pdp'],
        limit: 6,
      }),
      fetchHistoryEntries({
        kind: ['plp', 'search'],
        limit: 6,
      }),
    ])

    historyEntries.value = [...pdpEntries, ...plpAndSearchEntries]
  }

  // Invalidate the cached recently viewed pages whenever the browsing history changes.
  const shouldReadFromCache = ref(false)
  const unhookVisitRecorded = app.hook('browsing-history:update', () => {
    shouldReadFromCache.value = false
  })
  onScopeDispose(unhookVisitRecorded)

  return useAsyncData(
    'recently-viewed-pages',
    async () => {
      if (
        !isBrowsingHistoryEnabled ||
        !algoliaClient.value ||
        !configuration.value
      )
        return null

      await fetchAllHistoryEntries()

      const pdpIndexName =
        configuration.value.backboxWinners.indexes.active.name
      const plpIndexName = configuration.value.landingPages.indexes.active.name

      const requests = historyEntries.value.flatMap((entry) => {
        if (entry.kind === 'pdp') {
          return [
            {
              indexName: pdpIndexName,
              objectID: `${entry.productId}_${entry.grade}_${entry.offerType}`,
            },
          ]
        }

        if (entry.kind === 'plp') {
          return [
            {
              indexName: plpIndexName,
              objectID: entry.landingId,
            },
          ]
        }

        return []
      })

      if (requests.length === 0) return null

      try {
        const { results } = await algoliaClient.value.multipleGetObjects<
          AlgoliaProduct | AlgoliaLink | null
        >(requests)

        return results
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(
          '[recently-viewed-pages] multipleGetObjects failed',
          error,
        )

        throw error
      } finally {
        shouldReadFromCache.value = true
      }
    },
    {
      default: () => [] as RecentlyViewedPage[],
      transform: (results) => {
        const hitsByObjectId = new Map(
          (results ?? [])
            .filter((hit) => hit !== null)
            .map((hit) => [hit.objectID, hit]),
        )

        return historyEntries.value.flatMap<RecentlyViewedPage>((entry) => {
          switch (entry.kind) {
            case 'search': {
              return [
                {
                  id: entry.id,
                  highlightedTitle: entry.term,
                  title: entry.term,
                  url: resolveLocalizedRoute({
                    name: PRODUCT_LIST_ROUTES.SEARCH_RESULTS,
                    query: {
                      q: entry.term,
                    },
                  }),
                  entryType: 'search-query',
                  timestamp: entry.lastVisitedAt,
                },
              ]
            }
            case 'plp': {
              const hit = hitsByObjectId.get(entry.landingId) as
                | AlgoliaLink
                | undefined

              if (!hit) return []

              return [
                {
                  ...algoliaHitToLink(hit),
                  entryType: 'plp',
                  timestamp: entry.lastVisitedAt,
                },
              ]
            }
            case 'pdp': {
              const objectID = `${entry.productId}_${entry.grade}_${entry.offerType}`
              const hit = hitsByObjectId.get(objectID) as
                | AlgoliaProduct
                | undefined

              if (!hit) return []

              return [
                {
                  ...algoliaHitToProduct(hit),
                  entryType: 'pdp',
                  timestamp: entry.lastVisitedAt,
                },
              ]
            }
            default:
              return []
          }
        })
      },
      server: false,
      immediate: false,
      getCachedData: (key, nuxtApp) => {
        if (!shouldReadFromCache.value) {
          shouldReadFromCache.value = true
          return undefined
        }

        return nuxtApp.payload.data[key] || undefined
      },
      dedupe: 'defer',
    },
  )
}
